/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useCallback, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessages} from 'react-intl'
import {
    AspectRatio,
    Box,
    Flex,
    Skeleton as ChakraSkeleton,
    Text,
    useMultiStyleConfig,
    useDisclosure,
    Button,
} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import GalleryBody from './partials/gallery-body'
import GalleryModal from './partials/gallery-modal'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import {parseAndSanitizeJSON} from '../../utils/utils'
import {LARGE} from './partials/utils'
import {
    GalleryBucketFeaturesIcon,
    GalleryBucketInTheBoxIcon,
    GalleryBucketIntroIcon,
    GalleryBucketProductIcon,
} from '../icons'

const messages = defineMessages({
    productLabel: {
        id: 'gallery.bucket-product',
        defaultMessage: 'Product',
    },
    introLabel: {
        id: 'gallery.bucket-intro',
        defaultMessage: 'Intro',
    },
    featuresLabel: {
        id: 'gallery.bucket-features',
        defaultMessage: 'Features',
    },
    inTheBoxLabel: {
        id: 'gallery.bucket-in-the-box',
        defaultMessage: "What's in the box",
    },
    bucketsLabel: {
        id: 'gallery.bucket-list-label',
        defaultMessage: 'Product media categories',
    },
})

// Normalises any incoming media array into a consistent shape.
// Items without a resolvable image link are filtered out.
const normalizeGalleryMediaItems = (items = []) =>
    (Array.isArray(items) ? items : [])
        .map((item, index) => {
            const imageLink = item?.imageLink || item?.medialink || item?.url || item?.link || ''
            return {
                ...item,
                alt: item?.alt || item?.title || `Gallery media ${index + 1}`,
                title: item?.title || `Gallery media ${index + 1}`,
                imageLink,
                url: imageLink,
            }
        })
        .filter((item) => Boolean(item?.imageLink || item?.url))

/**
 * Skeleton placeholder rendered while product data is loading.
 */
export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})
    return (
        <Box data-testid="sf-image-gallery-skeleton" mb={{base: remCalc(10), lg: 0}}>
            <Flex flexDirection={{base: 'column-reverse', lg: 'row'}}>
                <Box width="100%">
                    <AspectRatio ratio={1} {...styles.heroImageSkeleton}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
            </Flex>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool,
}

/**
 * ImageGallery renders a single swiper that contains all media across every bucket
 * (product images, power images, features, in-the-box, intro videos).
 *
 * Bucket tabs act purely as navigation controls: clicking one jumps the swiper to
 * the first slide belonging to that bucket.  The active bucket is derived from the
 * current slide index rather than being stored as explicit state, so the two are
 * always in sync without extra bookkeeping.
 */
const ImageGallery = ({
    imageGroups = [],
    powerImages = [],
    inTheBoxImages = [],
    featureImages = [],
    selectedVariationAttributes = {},
    lazy = false,
    videoUrl,
    subModelIdentifier = '',
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ImageGallery')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [isModalGallerySwiper, setIsModalGallerySwiper] = useState(true)
    const [modalInitValue, setModalInitValue] = useState(0)

    // Selected slide index is lifted here so that bucket tabs can drive the swiper
    // and the active-bucket indicator can be derived from it without a separate state.
    const [selectedIndex, setSelectedIndex] = useState(0)

    // Reset to the first slide whenever the user picks a different variant,
    // because the product image set may change entirely.
    // Compared by value: the parent passes a new object reference on every render.
    const variationAttributesKey = JSON.stringify(selectedVariationAttributes)
    useEffect(() => {
        setSelectedIndex(0)
    }, [variationAttributesKey])

    // --- Normalised image arrays --------------------------------------------------

    const normalizedPowerImages = useMemo(
        () => normalizeGalleryMediaItems(powerImages),
        [powerImages],
    )

    const normalizedFeatureImages = useMemo(
        () =>
            normalizeGalleryMediaItems(featureImages).map((item) => ({
                ...item,
                bucketType: 'features',
            })),
        [featureImages],
    )

    const normalizedInTheBoxImages = useMemo(
        () =>
            normalizeGalleryMediaItems(inTheBoxImages).map((item) => ({
                ...item,
                bucketType: 'inTheBox',
            })),
        [inTheBoxImages],
    )

    const playableVideos = parseAndSanitizeJSON(videoUrl)?.filter((item) => item?.videolink) || []

    // --- Single flat image array passed to GalleryBody ---------------------------
    //
    // GalleryBody merges imageGroups (product LARGE/SMALL) with the powerImages prop
    // internally, so we only need to append the extra buckets here.
    //
    // Final slide order inside the swiper:
    //   [0 .. productImgCount-1]          product images (from imageGroups)
    //   [productImgCount .. +powerLen-1]  extra power images
    //   [.. +featuresLen-1]               features images
    //   [.. +inTheBoxLen-1]               in-the-box images
    //   [thumbnailImages.length]          intro video (appended by GalleryBody)

    const allPowerImages = useMemo(
        () => [...normalizedPowerImages, ...normalizedFeatureImages, ...normalizedInTheBoxImages],
        [normalizedPowerImages, normalizedFeatureImages, normalizedInTheBoxImages],
    )

    // --- Bucket offsets inside the flat slide array ------------------------------
    //
    // We need to know where each bucket starts so that tab clicks can jump the
    // swiper to the correct position and so that the active bucket can be derived
    // from selectedIndex.

    const productImageCount = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes,
            })?.images?.length || 0,
        [imageGroups, selectedVariationAttributes],
    )

    const bucketOffsets = useMemo(() => {
        const productEnd = productImageCount + normalizedPowerImages.length
        const featuresEnd = productEnd + normalizedFeatureImages.length
        const inTheBoxEnd = featuresEnd + normalizedInTheBoxImages.length
        return {
            product: 0,
            features: productEnd,
            inTheBox: featuresEnd,
            // videos are always appended last by GalleryBody (after all image slides)
            intro: inTheBoxEnd,
        }
    }, [
        productImageCount,
        normalizedPowerImages,
        normalizedFeatureImages,
        normalizedInTheBoxImages,
    ])

    // --- Bucket metadata (label / icon / slide count) ----------------------------

    const mediaBuckets = useMemo(
        () => [
            {
                id: 'product',
                label: intl.formatMessage(messages.productLabel),
                icon: GalleryBucketProductIcon,
                count: productImageCount + normalizedPowerImages.length,
            },
            {
                id: 'features',
                label: intl.formatMessage(messages.featuresLabel),
                icon: GalleryBucketFeaturesIcon,
                count: normalizedFeatureImages.length,
            },
            {
                id: 'inTheBox',
                label: intl.formatMessage(messages.inTheBoxLabel),
                icon: GalleryBucketInTheBoxIcon,
                count: normalizedInTheBoxImages.length,
            },
            {
                id: 'intro',
                label: intl.formatMessage(messages.introLabel),
                icon: GalleryBucketIntroIcon,
                count: playableVideos.length,
            },
        ],
        [
            intl,
            productImageCount,
            normalizedPowerImages,
            normalizedFeatureImages,
            normalizedInTheBoxImages,
            playableVideos,
        ],
    )

    const availableBuckets = useMemo(() => mediaBuckets.filter((b) => b.count > 0), [mediaBuckets])

    // --- Active bucket derived from slide position -------------------------------
    //
    // Walk the bucket list in reverse order and return the first bucket whose offset
    // is less than or equal to the current slide index.

    const activeBucketId = useMemo(() => {
        const reversed = [...mediaBuckets].reverse()
        for (const bucket of reversed) {
            if (bucket.count > 0 && selectedIndex >= bucketOffsets[bucket.id]) {
                return bucket.id
            }
        }
        return mediaBuckets[0]?.id || 'product'
    }, [selectedIndex, bucketOffsets, mediaBuckets])

    const shouldShowBucketTabs = !(
        availableBuckets.length === 1 && availableBuckets[0]?.id === 'product'
    )
    const activeBucketTabId = `gallery-media-tab-${activeBucketId}`
    const galleryPanelId = 'gallery-media-panel'

    // --- Modal ------------------------------------------------------------------

    const openModal = useCallback(
        (index) => {
            setModalInitValue(typeof index === 'number' ? index : selectedIndex)
            setIsModalGallerySwiper(true)
            onOpen()
            document.documentElement.classList.add('img-gallery-modal-open')
        },
        [selectedIndex, onOpen],
    )

    const closeModal = useCallback(() => {
        setIsModalGallerySwiper(true)
        onClose()
        document.documentElement.classList.remove('img-gallery-modal-open')
    }, [onClose])

    // --- Bucket tab interaction --------------------------------------------------

    // Jump the swiper to the first slide of the selected bucket.
    const handleBucketSelect = useCallback(
        (bucketId) => {
            const bucket = mediaBuckets.find((b) => b.id === bucketId)
            if (!bucket || bucket.count === 0) return
            if (bucketId === activeBucketId) return

            const offset = bucketOffsets[bucketId] ?? 0
            setSelectedIndex(offset)

            if (isOpen) closeModal()
        },
        [activeBucketId, bucketOffsets, mediaBuckets, isOpen, closeModal],
    )

    // Arrow-key / Home / End navigation across tabs, per ARIA tab-list pattern.
    const handleBucketKeyDown = useCallback(
        (event, bucketId) => {
            if (availableBuckets.length <= 1) return

            const activeIndex = availableBuckets.findIndex((b) => b.id === bucketId)
            if (activeIndex < 0) return

            let targetIndex = activeIndex

            if (event.key === 'ArrowRight') {
                targetIndex = (activeIndex + 1) % availableBuckets.length
            } else if (event.key === 'ArrowLeft') {
                targetIndex = (activeIndex - 1 + availableBuckets.length) % availableBuckets.length
            } else if (event.key === 'Home') {
                targetIndex = 0
            } else if (event.key === 'End') {
                targetIndex = availableBuckets.length - 1
            } else {
                return
            }

            event.preventDefault()
            const targetBucketId = availableBuckets[targetIndex]?.id
            if (!targetBucketId) return

            handleBucketSelect(targetBucketId)
            document.getElementById(`gallery-media-tab-${targetBucketId}`)?.focus()
        },
        [availableBuckets, handleBucketSelect],
    )

    // Clean up the body class if the component unmounts while the modal is open.
    useEffect(() => {
        return () => {
            document.documentElement.classList.remove('img-gallery-modal-open')
        }
    }, [])

    // --- Cross-bucket boundary navigation ---------------------------------------
    //
    // Called by GalleryBody when the user tries to navigate past the last (or
    // before the first) slide of the current bucket.  We advance to the adjacent
    // bucket and position the swiper at its first or last slide accordingly.

    const handleBoundaryNavigate = useCallback(
        (moveRight, shouldLoop = false) => {
            if (availableBuckets.length <= 1) return false

            const activeIndex = availableBuckets.findIndex((b) => b.id === activeBucketId)
            if (activeIndex < 0) return false

            let targetIndex = activeIndex + (moveRight ? 1 : -1)

            if (targetIndex < 0 || targetIndex >= availableBuckets.length) {
                if (!shouldLoop) return false
                targetIndex = (targetIndex + availableBuckets.length) % availableBuckets.length
            }

            const targetBucket = availableBuckets[targetIndex]
            if (!targetBucket) return false

            const offset = bucketOffsets[targetBucket.id] ?? 0
            // When moving forward land on the first slide; when moving back land on the last.
            const jumpIndex = moveRight ? offset : offset + targetBucket.count - 1
            setSelectedIndex(jumpIndex)
            return true
        },
        [availableBuckets, activeBucketId, bucketOffsets],
    )

    // ---------------------------------------------------------------------------

    return (
        <Flex flexDir="column" data-ab="gallery-collapsed" data-cs-label="gallery-collapsed">
            <Box {...(styles?.galleryFrame || {})}>
                {/*
                 * Stable key keeps the swiper mounted across bucket changes.
                 * selectedIndex (controlled) drives which slide is shown;
                 * onSelectedIndexChange keeps this component in sync when the
                 * user swipes manually inside GalleryBody.
                 */}
                <GalleryBody
                    key="main-gallery"
                    imageGroups={imageGroups}
                    selectedVariationAttributes={selectedVariationAttributes}
                    lazy={lazy}
                    openModal={openModal}
                    variant="inPage"
                    videoUrl={videoUrl}
                    setIsModalGallerySwiper={setIsModalGallerySwiper}
                    isModalGallerySwiper={isModalGallerySwiper}
                    powerImages={allPowerImages}
                    subModelIdentifier={subModelIdentifier}
                    selectedIndex={selectedIndex}
                    onSelectedIndexChange={setSelectedIndex}
                    onBoundaryNavigate={(moveRight) => handleBoundaryNavigate(moveRight, true)}
                    panelId={galleryPanelId}
                    activeTabId={activeBucketTabId}
                    tabsContent={
                        shouldShowBucketTabs ? (
                            <Flex
                                role="tablist"
                                aria-label={intl.formatMessage(messages.bucketsLabel)}
                                data-testid="gallery-media-buckets"
                                {...styles.bucketTabs}
                            >
                                {availableBuckets.map((bucket) => {
                                    const IconComponent = bucket.icon
                                    const isActive = activeBucketId === bucket.id
                                    return (
                                        <Button
                                            key={bucket.id}
                                            id={`gallery-media-tab-${bucket.id}`}
                                            role="tab"
                                            aria-selected={isActive}
                                            aria-controls={galleryPanelId}
                                            tabIndex={isActive ? -1 : 0}
                                            variant="ghost"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleBucketSelect(bucket.id)
                                            }}
                                            onKeyDown={(e) => {
                                                e.stopPropagation()
                                                handleBucketKeyDown(e, bucket.id)
                                            }}
                                            {...styles.bucketTab}
                                            {...(isActive ? styles.bucketTabActive : {})}
                                            data-testid={`gallery-media-bucket-${bucket.id}`}
                                        >
                                            <Flex {...styles.bucketTabContent}>
                                                <IconComponent {...styles.bucketTabIcon} />
                                                <Text {...styles.bucketTabLabel}>
                                                    {bucket.label}
                                                </Text>
                                                <Text {...styles.bucketTabCounter}>
                                                    {String(bucket.count).padStart(2, '0')}
                                                </Text>
                                            </Flex>
                                        </Button>
                                    )
                                })}
                            </Flex>
                        ) : null
                    }
                />
            </Box>

            <GalleryModal
                key="all-media-modal"
                isOpen={isOpen}
                closeModal={closeModal}
                imageGroups={imageGroups}
                selectedVariationAttributes={selectedVariationAttributes}
                lazy={lazy}
                openModal={openModal}
                videoUrl={videoUrl}
                isModalGallerySwiper={isModalGallerySwiper}
                setIsModalGallerySwiper={setIsModalGallerySwiper}
                modalInitValue={modalInitValue}
                powerImages={allPowerImages}
                initialSelectedIndex={modalInitValue}
                onBoundaryNavigate={() => false}
            />
        </Flex>
    )
}

ImageGallery.propTypes = {
    /** All image groups for the product (large + small view types). */
    imageGroups: PropTypes.array,
    /** Extra hero-quality images that follow the standard product images. */
    powerImages: PropTypes.array,
    /** Images displayed in the "in the box" bucket. */
    inTheBoxImages: PropTypes.array,
    /** Images displayed in the "features" bucket. */
    featureImages: PropTypes.array,
    /** Currently selected variation attributes used to pick the right image group. */
    selectedVariationAttributes: PropTypes.object,
    /** Size token forwarded to the Chakra multi-style config. */
    size: PropTypes.string,
    /** When true images are loaded lazily. */
    lazy: PropTypes.bool,
    /** JSON string describing intro / YouTube videos. */
    videoUrl: PropTypes.string,
    /** Sub-model identifier forwarded to the detail gallery. */
    subModelIdentifier: PropTypes.string,
}

export default ImageGallery
